import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import { SCOPE_KEY_PREFIX } from "./scope";

interface ScopeState {
  /** The provider namespace the operator picked, or null for their own. */
  selected: string | null;
  select: (ns: string | null) => void;
}

const ScopeContext = createContext<ScopeState>({ selected: null, select: () => undefined });

function read(): string | null {
  try {
    return window.sessionStorage.getItem(SCOPE_KEY_PREFIX + "selected");
  } catch {
    return null;
  }
}
function write(ns: string | null) {
  try {
    if (ns) window.sessionStorage.setItem(SCOPE_KEY_PREFIX + "selected", ns);
    else window.sessionStorage.removeItem(SCOPE_KEY_PREFIX + "selected");
  } catch {
    /* a private window forgets on reload; the root is the fallback */
  }
}

/**
 * Holds the picked provider for the tab. Per TAB (sessionStorage), so two
 * tabs can be inside two providers, and a new tab starts at the operator's
 * own namespace. Validation against the token happens in useIdentity, not
 * here: this stores a wish, useIdentity decides whether it is allowed.
 */
export function ScopeProvider({ children }: { children: ReactNode }) {
  const [selected, setSelected] = useState<string | null>(read);
  const select = useCallback((ns: string | null) => {
    write(ns);
    setSelected(ns);
  }, []);
  const value = useMemo(() => ({ selected, select }), [selected, select]);
  return <ScopeContext.Provider value={value}>{children}</ScopeContext.Provider>;
}

export function useScopeSelection(): ScopeState {
  return useContext(ScopeContext);
}
